import { useCallback, useRef } from "react";
import { ACTIVITY_DOCK_DRAG_THRESHOLD_PX } from "./activityDockPointerConstants";
import { FIREFLY_PTT_HOLD_MS } from "@/lib/firefly/fireflyPttConstants";

/** Long-press PTT is touch-primary / coarse-pointer only (§3.1). */
export function isPttLongPressEligible(
  pointerType: string,
  matchMedia: (query: string) => MediaQueryList = (q) => window.matchMedia(q),
): boolean {
  if (pointerType === "touch") return true;
  return matchMedia("(pointer: coarse)").matches;
}

export function crossedActivityDockDragThreshold(
  dx: number,
  dy: number,
  thresholdPx: number = ACTIVITY_DOCK_DRAG_THRESHOLD_PX,
): boolean {
  return dx >= thresholdPx || dy >= thresholdPx;
}

export type ActivityDockPointerDown = {
  x: number;
  y: number;
  moved: boolean;
};

export type ActivityDockPttGestureBridge = {
  onPointerDown: (pointerType: string) => void;
  onPointerMove: (down: ActivityDockPointerDown, clientX: number, clientY: number) => void;
  onPointerUp: () => boolean;
  onPointerCancel: () => void;
};

export type UseActivityDockPttGestureArgs = {
  enabled: boolean;
  onPttStart: () => void;
  onPttEnd: () => void;
  onPttCancel: () => void;
  holdMs?: number;
  scheduleTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (id: ReturnType<typeof setTimeout>) => void;
};

/** Pure bridge factory — used by tests and `useActivityDockPttGesture`. */
export function createActivityDockPttGestureBridge({
  enabled,
  onPttStart,
  onPttEnd,
  onPttCancel,
  holdMs = FIREFLY_PTT_HOLD_MS,
  scheduleTimer = setTimeout,
  clearTimer = clearTimeout,
}: UseActivityDockPttGestureArgs): ActivityDockPttGestureBridge {
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let pttActive = false;

  const clearHoldTimer = () => {
    if (holdTimer == null) return;
    clearTimer(holdTimer);
    holdTimer = null;
  };

  return {
    onPointerDown(pointerType: string) {
      if (!enabled || !isPttLongPressEligible(pointerType)) return;
      clearHoldTimer();
      holdTimer = scheduleTimer(() => {
        holdTimer = null;
        pttActive = true;
        onPttStart();
      }, holdMs);
    },

    onPointerMove(down, clientX, clientY) {
      const dx = Math.abs(clientX - down.x);
      const dy = Math.abs(clientY - down.y);
      if (!crossedActivityDockDragThreshold(dx, dy)) return;
      clearHoldTimer();
      if (pttActive) {
        pttActive = false;
        onPttCancel();
      }
    },

    onPointerUp() {
      clearHoldTimer();
      if (!pttActive) return false;
      pttActive = false;
      onPttEnd();
      return true;
    },

    onPointerCancel() {
      clearHoldTimer();
      if (pttActive) {
        pttActive = false;
        onPttCancel();
      }
    },
  };
}

export function useActivityDockPttGesture({
  enabled,
  onPttStart,
  onPttEnd,
  onPttCancel,
  holdMs = FIREFLY_PTT_HOLD_MS,
}: UseActivityDockPttGestureArgs): ActivityDockPttGestureBridge {
  const enabledRef = useRef(enabled);
  const onPttStartRef = useRef(onPttStart);
  const onPttEndRef = useRef(onPttEnd);
  const onPttCancelRef = useRef(onPttCancel);
  const holdMsRef = useRef(holdMs);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pttActiveRef = useRef(false);

  enabledRef.current = enabled;
  onPttStartRef.current = onPttStart;
  onPttEndRef.current = onPttEnd;
  onPttCancelRef.current = onPttCancel;
  holdMsRef.current = holdMs;

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current == null) return;
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = null;
  }, []);

  const cancelActivePtt = useCallback(() => {
    if (!pttActiveRef.current) return;
    pttActiveRef.current = false;
    onPttCancelRef.current();
  }, []);

  const onPointerDown = useCallback(
    (pointerType: string) => {
      if (!enabledRef.current || !isPttLongPressEligible(pointerType)) return;
      clearHoldTimer();
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        pttActiveRef.current = true;
        onPttStartRef.current();
      }, holdMsRef.current);
    },
    [clearHoldTimer],
  );

  const onPointerMove = useCallback(
    (down: ActivityDockPointerDown, clientX: number, clientY: number) => {
      const dx = Math.abs(clientX - down.x);
      const dy = Math.abs(clientY - down.y);
      if (!crossedActivityDockDragThreshold(dx, dy)) return;
      clearHoldTimer();
      if (pttActiveRef.current) {
        pttActiveRef.current = false;
        onPttCancelRef.current();
      }
    },
    [clearHoldTimer],
  );

  const onPointerUp = useCallback(() => {
    clearHoldTimer();
    if (!pttActiveRef.current) return false;
    pttActiveRef.current = false;
    onPttEndRef.current();
    return true;
  }, [clearHoldTimer]);

  const onPointerCancel = useCallback(() => {
    clearHoldTimer();
    cancelActivePtt();
  }, [cancelActivePtt, clearHoldTimer]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
